/**
 * /m — mobile locations list. Same query (and cache entry) as the
 * desktop Locations page, rendered as a lean single-column card list
 * with a search box. Tapping a location opens its device list.
 */
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { Badge } from "../components/Badge";

type Location = {
  entity_id: string;
  name: string | null;
  address: string | null;
  region: string | null;
  chassis_total: number | null;
  chassis_managed: number | null;
  is_placeholder: boolean;
};
type LocationsResponse = { locations: Location[] };

export function MLocations() {
  const [filter, setFilter] = useState("");
  const q = useQuery<LocationsResponse>({
    queryKey: ["v2", "locations"],
    queryFn: async () =>
      (await api.get<LocationsResponse>("/gigafy/locations")).data,
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    const all = q.data?.locations ?? [];
    const needle = filter.trim().toLowerCase();
    return all
      .filter((l) => {
        // Field techs care about sites with hardware; a search still
        // pierces the filter so unactivated sites stay findable.
        if (!needle && !l.is_placeholder && (l.chassis_total ?? 0) === 0)
          return false;
        if (!needle) return true;
        return (
          (l.name ?? "").toLowerCase().includes(needle) ||
          (l.address ?? "").toLowerCase().includes(needle) ||
          (l.region ?? "").toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [q.data, filter]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
        <input
          type="search"
          placeholder="Find a site…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-900 py-3 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
        />
      </div>
      {q.isLoading ? (
        <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">
          No sites match.
        </p>
      ) : (
        filtered.map((l) => (
          <Link
            key={l.entity_id}
            to={`/m/locations/${l.entity_id}`}
            className="flex min-h-[56px] flex-col justify-center gap-0.5 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 active:bg-slate-800"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium text-slate-100">
                {l.name ?? `Location ${l.entity_id.slice(0, 8)}…`}
              </span>
              {(l.chassis_total ?? 0) > 0 && (
                <Badge tone="info">
                  {l.chassis_managed ?? 0}/{l.chassis_total} chassis
                </Badge>
              )}
            </span>
            {l.address && (
              <span className="truncate text-xs text-slate-500">
                {l.address}
              </span>
            )}
          </Link>
        ))
      )}
    </div>
  );
}
